/**
 * Port of C# GeneratePreprocessMetadata.cs - JSON Key Placeholder Processing
 * Parses JSON key placeholders within preprocessed templates
 */

import { CommonUtil } from '../../common/commonUtil.js';
import { JsonKeyPlaceholder } from '../../model/index.js';
import type { PreprocessedTemplate } from '../../model/index.js';

// #region JSON Key Placeholder Processing (Private)

/**
 * Parses JSON key placeholders ({{$KeyName}} and {{!$KeyName}}) in the content
 * and adds them to the preprocessed template
 */
export function parseJsonKeyPlaceholders(content: string, template: PreprocessedTemplate): void {
    let searchPos = 0;

    while (searchPos < content.length) {
        // Look for {{$ or {{!$
        const dollarStart = content.indexOf('{{$', searchPos);
        const skipDollarStart = content.indexOf('{{!$', searchPos);

        // Determine which comes first
        let isSkipMarker = false;
        let openStart = -1;

        if (dollarStart !== -1 && skipDollarStart !== -1) {
            if (skipDollarStart < dollarStart) {
                openStart = skipDollarStart;
                isSkipMarker = true;
            } else {
                openStart = dollarStart;
                isSkipMarker = false;
            }
        } else if (dollarStart !== -1) {
            openStart = dollarStart;
            isSkipMarker = false;
        } else if (skipDollarStart !== -1) {
            openStart = skipDollarStart;
            isSkipMarker = true;
        }

        if (openStart === -1) break;

        // Calculate offset based on whether skip marker is present
        const nameOffset = isSkipMarker ? 4 : 3;  // "{{!$" or "{{$"

        // Find closing }}
        const closeStart = content.indexOf('}}', openStart + nameOffset);
        if (closeStart === -1) break;

        // Extract key name
        const keyName = content.substring(openStart + nameOffset, closeStart).trim();
        if (!keyName || !CommonUtil.isAlphaNumeric(keyName)) {
            searchPos = openStart + nameOffset;
            continue;
        }

        // Skip HTMLPLACEHOLDER framework keys - these are slot merge placeholders, not JSON keys
        if (keyName.toUpperCase().startsWith('HTMLPLACEHOLDER')) {
            searchPos = closeStart + 2;
            continue;
        }

        // Create JSON key placeholder structure
        const jsonKeyPlaceholder = new JsonKeyPlaceholder();
        jsonKeyPlaceholder.keyName = keyName;
        jsonKeyPlaceholder.startIndex = openStart;
        jsonKeyPlaceholder.endIndex = closeStart + 2;
        jsonKeyPlaceholder.fullMatch = content.substring(openStart, closeStart + 2);

        // Add to appropriate list based on skip marker
        if (isSkipMarker) {
            template.jsonSkipPlaceholders.push(jsonKeyPlaceholder);
        } else {
            template.jsonKeyPlaceholders.push(jsonKeyPlaceholder);
        }

        searchPos = closeStart + 2;
    }
}

// #endregion
